import { z } from 'zod';

/**
 * Admin-managed bots (NPC civilizations): behaviour configuration.
 *
 * The configuration only tunes *how eager* a bot is (decision cadence, willingness to spend, how far it
 * expands, how aggressive it is) — never the game rules: costs, travel times, protection and fog of war
 * are enforced by the same services a player's request goes through. The values are plain data so that
 * the pure brain (`@galaxis/simulation`) receives them as a parameter and stays reproducible.
 *
 * Storage: `npc_bot_defaults` (global default, a full config) and `npc_profiles.config` (per-bot overrides,
 * a partial config). `NPC_BOT_CONFIG_VERSION` is stored next to them; `parseStoredNpcOverrides` migrates.
 */
export const NPC_BOT_CONFIG_VERSION = 1;

export const NPC_PERSONALITIES = ['expansionist', 'militarist', 'trader'] as const;
export type NpcBotPersonality = (typeof NPC_PERSONALITIES)[number];

export const NPC_DIFFICULTIES = ['beginner', 'normal', 'hard', 'obsessed'] as const;
export type NpcDifficulty = (typeof NPC_DIFFICULTIES)[number];

/** auto = the personality's own behaviour (what NPCs did before this setting existed). */
export const NPC_AGGRESSIONS = ['auto', 'defensive', 'watchful', 'opportunist', 'aggressive'] as const;
export type NpcAggression = (typeof NPC_AGGRESSIONS)[number];

export const NPC_DIPLOMACY_STYLES = ['isolated', 'auto', 'friendly'] as const;
export type NpcDiplomacyStyle = (typeof NPC_DIPLOMACY_STYLES)[number];

export const NPC_RESEARCH_FOCI = ['auto', 'economy', 'science', 'exploration', 'diplomacy', 'military'] as const;
export type NpcResearchFocus = (typeof NPC_RESEARCH_FOCI)[number];

export const NPC_WARSHIP_CLASSES = ['corvette', 'frigate', 'cruiser'] as const;
export type NpcWarshipClass = (typeof NPC_WARSHIP_CLASSES)[number];

export const NPC_LIMITS = {
  tempo: { min: 0.25, max: 2, step: 0.05 },
  thrift: { min: 0.25, max: 3, step: 0.05 },
  navySize: { min: 0.25, max: 2, step: 0.05 },
  maxSystems: { min: 1, max: 150 },
  playerGraceHours: { min: 0, max: 336 },
  /** Most bots one request may create / one world may hold. */
  createMax: 20,
  totalMax: 40,
} as const;

export const NpcBotConfigSchema = z.object({
  /** Decision cadence multiplier: 2 = the bot decides twice as often (turn interval ÷ 2). The game clock itself is untouched. */
  tempo: z.number().min(NPC_LIMITS.tempo.min).max(NPC_LIMITS.tempo.max),
  /** Reserve multiplier: how much metal the bot keeps back before it buys. Low = spends eagerly, high = hoards. */
  thrift: z.number().min(NPC_LIMITS.thrift.min).max(NPC_LIMITS.thrift.max),
  /** Multiplier on the warship count the bot aims for. */
  navySize: z.number().min(NPC_LIMITS.navySize.min).max(NPC_LIMITS.navySize.max),
  /** Hard ceiling on owned systems (null = no ceiling beyond the game's own economics). */
  maxSystems: z.number().int().min(NPC_LIMITS.maxSystems.min).max(NPC_LIMITS.maxSystems.max).nullable(),
  aggression: z.enum(NPC_AGGRESSIONS),
  /** Extra protection for player civilizations on top of the standard newcomer protection: hours since founding. */
  playerGraceHours: z.number().int().min(NPC_LIMITS.playerGraceHours.min).max(NPC_LIMITS.playerGraceHours.max),
  diplomacy: z.enum(NPC_DIPLOMACY_STYLES),
  researchFocus: z.enum(NPC_RESEARCH_FOCI),
  /** Warship classes the bot may build (it never builds a class outside this list). */
  shipClasses: z.array(z.enum(NPC_WARSHIP_CLASSES)).min(1).max(3).refine(a => new Set(a).size === a.length, 'Egy hajóosztály csak egyszer szerepelhet'),
  /** Whether this bot may use the language-model diplomat when the global switch is on (it is off by default). */
  llmDiplomacy: z.boolean(),
}).strict();
export type NpcBotConfig = z.infer<typeof NpcBotConfigSchema>;

export const NpcBotOverridesSchema = NpcBotConfigSchema.partial().strict();
export type NpcBotOverrides = z.infer<typeof NpcBotOverridesSchema>;

const ALL_CLASSES: NpcWarshipClass[] = ['corvette', 'frigate', 'cruiser'];

/**
 * Presets. `normal` reproduces the behaviour NPCs had before the setting existed (tempo 1, reserve ×1,
 * personality-driven aggression, no ceiling) — existing bots keep playing exactly as before.
 */
export const NPC_PRESETS: Record<NpcDifficulty, NpcBotConfig> = {
  beginner: { tempo: 0.5, thrift: 1.6, navySize: 0.5, maxSystems: 6, aggression: 'defensive', playerGraceHours: 72, diplomacy: 'friendly', researchFocus: 'auto', shipClasses: ['corvette', 'frigate'], llmDiplomacy: true },
  normal: { tempo: 1, thrift: 1, navySize: 1, maxSystems: null, aggression: 'auto', playerGraceHours: 0, diplomacy: 'auto', researchFocus: 'auto', shipClasses: [...ALL_CLASSES], llmDiplomacy: true },
  hard: { tempo: 1.5, thrift: 0.75, navySize: 1, maxSystems: null, aggression: 'auto', playerGraceHours: 0, diplomacy: 'auto', researchFocus: 'auto', shipClasses: [...ALL_CLASSES], llmDiplomacy: true },
  obsessed: { tempo: 2, thrift: 0.5, navySize: 1.15, maxSystems: null, aggression: 'aggressive', playerGraceHours: 0, diplomacy: 'auto', researchFocus: 'auto', shipClasses: [...ALL_CLASSES], llmDiplomacy: true },
};

export const NPC_DIFFICULTY_LABELS: Record<NpcDifficulty | 'custom', string> = {
  beginner: 'Kezdő', normal: 'Normál', hard: 'Nehéz', obsessed: 'Megszállott', custom: 'Egyéni',
};

export const defaultNpcConfig = (): NpcBotConfig => structuredCloneConfig(NPC_PRESETS.normal);

export function structuredCloneConfig(c: NpcBotConfig): NpcBotConfig {
  return { ...c, shipClasses: [...c.shipClasses] };
}

/** Effective configuration = global default, then the bot's own overrides. */
export function resolveNpcConfig(defaults: NpcBotConfig, overrides: NpcBotOverrides | null | undefined): NpcBotConfig {
  return { ...structuredCloneConfig(defaults), ...(overrides ?? {}) } as NpcBotConfig;
}

const sameClasses = (a: readonly string[], b: readonly string[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

/** The preset a configuration equals exactly, else 'custom'. */
export function detectNpcDifficulty(c: NpcBotConfig): NpcDifficulty | 'custom' {
  for (const d of NPC_DIFFICULTIES) {
    const p = NPC_PRESETS[d];
    if (p.tempo === c.tempo && p.thrift === c.thrift && p.navySize === c.navySize && p.maxSystems === c.maxSystems && p.aggression === c.aggression
      && p.playerGraceHours === c.playerGraceHours && p.diplomacy === c.diplomacy && p.researchFocus === c.researchFocus
      && sameClasses(p.shipClasses, c.shipClasses) && p.llmDiplomacy === c.llmDiplomacy) return d;
  }
  return 'custom';
}

/**
 * Reads a stored value (`{ v, overrides }` or a bare overrides object from an older shape). Anything unreadable
 * degrades to "no overrides" (= the global default) instead of failing a bot turn.
 */
export function parseStoredNpcOverrides(raw: unknown): NpcBotOverrides {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const body = ('overrides' in o && typeof o['overrides'] === 'object' ? o['overrides'] : o) as Record<string, unknown>;
  const known = Object.fromEntries(Object.entries(body).filter(([k]) => k in NpcBotConfigSchema.shape));
  const parsed = NpcBotOverridesSchema.safeParse(known);
  if (parsed.success) return parsed.data;
  // keep the valid fields, drop the rest (a field removed or tightened in a later version must not break the bot)
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(known)) {
    const one = NpcBotOverridesSchema.safeParse({ [k]: v });
    if (one.success) Object.assign(out, one.data);
  }
  return out as NpcBotOverrides;
}

export function parseStoredNpcDefaults(raw: unknown): NpcBotConfig {
  return resolveNpcConfig(defaultNpcConfig(), parseStoredNpcOverrides(raw));
}

/** ms between two turns of a bot: the game's NPC cadence divided by the bot's tempo. */
export function npcTurnIntervalMs(baseMs: number, tempo: number): number {
  return Math.max(1000, Math.round(baseMs / Math.min(NPC_LIMITS.tempo.max, Math.max(NPC_LIMITS.tempo.min, tempo))));
}

export const NPC_STATES = ['active', 'paused', 'withdrawing'] as const;
export type NpcBotState = (typeof NPC_STATES)[number];
